/**
 * PNG export helpers shared by both Step-4 renderers.
 *
 * - vis-network renders to a <canvas>, so we export it directly via
 *   `canvas.toDataURL` (same approach as `MermaidDiagram.tsx`).
 * - React Flow renders HTML+SVG, so we rasterize a DOM element with
 *   `html-to-image` (loaded lazily so it only ships when an export happens).
 */

/** Trigger a browser download for a data URL. */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Export a canvas element (vis-network) to PNG. Returns false if unavailable. */
export function exportCanvasToPng(
  canvas: HTMLCanvasElement | null | undefined,
  filename = 'graph.png'
): boolean {
  if (!canvas) return false;
  try {
    const url = canvas.toDataURL('image/png');
    downloadDataUrl(url, filename);
    return true;
  } catch (err) {
    console.error('Canvas PNG export failed', err);
    return false;
  }
}

export interface ExportElementOptions {
  backgroundColor?: string;
  pixelRatio?: number;
  /** Return false to exclude a DOM node from the capture (e.g. UI overlays). */
  filter?: (node: HTMLElement) => boolean;
}

/** Export an arbitrary DOM element (React Flow) to PNG via html-to-image. */
export async function exportElementToPng(
  element: HTMLElement | null | undefined,
  filename = 'graph.png',
  options: ExportElementOptions = {}
): Promise<boolean> {
  if (!element) return false;
  try {
    const { toPng } = await import('html-to-image');
    const dataUrl = await toPng(element, {
      backgroundColor: options.backgroundColor ?? '#ffffff',
      pixelRatio: options.pixelRatio ?? 2,
      filter: options.filter as ((node: HTMLElement) => boolean) | undefined,
      cacheBust: true,
    });
    downloadDataUrl(dataUrl, filename);
    return true;
  } catch (err) {
    console.error('Element PNG export failed', err);
    return false;
  }
}
